/**
 * The coach's prose renderer — markdown with the `rehypeLeadIns` plugin,
 * `poker-trainer://sheet/<id>` link interception, and the `.prose-chat`
 * accent styling.
 *
 * Lifted out of `MessageBubble` so both the conversational chat surface
 * AND the focused DrillView (`DrillFrame`) render the coach verdict
 * through ONE component — no drift between the two. Behavior is identical
 * to the inline block it replaced; `MessageBubble`'s tests stay green.
 */

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { rehypeLeadIns } from "@/features/chat/lib/messageFormat";

const SHEET_URL_PREFIX = "poker-trainer://sheet/";

interface VerdictProseProps {
  /** Already-trimmed assistant prose (markdown). */
  text: string;
  /** Tapping a `poker-trainer://sheet/<id>` link opens that study sheet. */
  onSheetLink?: (sheetId: string) => void;
}

export function VerdictProse({ text, onSheetLink }: VerdictProseProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeLeadIns]}
      // react-markdown's default urlTransform strips non-http(s)
      // schemes — `poker-trainer://...` would come through as an empty
      // href and our handler would never match. Allow it explicitly;
      // still block dangerous schemes.
      urlTransform={(url) => {
        const lower = url.toLowerCase();
        if (
          lower.startsWith("javascript:") ||
          lower.startsWith("vbscript:") ||
          lower.startsWith("data:")
        ) {
          return "";
        }
        return url;
      }}
      components={{
        a: ({ href, children }) => {
          if (href?.startsWith(SHEET_URL_PREFIX)) {
            const id = href.slice(SHEET_URL_PREFIX.length);
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onSheetLink?.(id);
                }}
                className="text-accent underline underline-offset-2 hover:no-underline font-medium"
              >
                {children}
              </button>
            );
          }
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline underline-offset-2 hover:no-underline"
            >
              {children}
            </a>
          );
        },
      }}
    >
      {text}
    </ReactMarkdown>
  );
}
